<template>
    <v-btn v-if="showProfileStatus" variant="text" class="my-profile-status px-2" :class="{ 'my-profile-status--hide-icon' : !showMyProfileIcon }" v-bind="vBtnProps" :href="url">
        <template #prepend>
            <v-sheet
                v-if="showMyProfileIcon"
                rounded="circle"
                width="20"
                height="20"
                class="d-flex align-center justify-center"
                :class="{ 'v-btn--highlighted': selectedMenu === -1 }"
            >
                <v-icon icon="mdi-account" size="18" :color="selectedMenu === -1 ? 'white' : 'green'" />
            </v-sheet>
        </template>
        <span v-if="!compactMode">{{ title }}</span>
    </v-btn>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { MenuStructure, useProfileNavigation, useSiteNavigation } from '../../composables';
import { ProfileInfo } from '../../models';

interface Props {
    menuItems: MenuStructure;
    isLoggedIn: boolean;
    colorTheme?: string;
    profileInfo?: ProfileInfo;
    currentLanguage: string;
    selectedMenuItem?: number;
    compactMode?: boolean;
    hideMyProfileIcon?: boolean;
}

const props = defineProps<Props>();
const selectedMenu = defineModel<number>('selectedMenuItem');

const { loginLink, logoutLink } = useSiteNavigation(props);
const { profilePageLink } = useProfileNavigation(props);

const canLogIn = computed(() => !props.isLoggedIn && loginLink.value);
const canLogOut = computed(() => props.isLoggedIn && logoutLink.value);

const showProfileStatus = computed(()=> { return !!(canLogOut.value || canLogIn.value); });
const url = computed(() => {
    if (canLogIn.value) return loginLink.value!.link;

    return undefined;
});
const showMyProfileIcon = computed(() => {
    if (props.hideMyProfileIcon && !canLogIn.value) return false;

    return true;
});

const title = computed(() => {
    if (props.isLoggedIn && logoutLink.value)
        return props.profileInfo?.firstName ? props.profileInfo.firstName : props.profileInfo?.displayName;

    if (!props.isLoggedIn && loginLink.value) return loginLink.value.displayName;

    return undefined;
});

const vBtnProps = computed(() => {
    return {
        href: canLogIn.value ? url.value : undefined,
        active: canLogOut.value && selectedMenu.value === -1,
        onClick: canLogOut.value ? () => (selectedMenu.value = -1) : undefined,
        class: { 'v-btn--slim': props.colorTheme == 'inverted', 'my-profile-status--compact': props.compactMode }
    };
});
</script>

<style lang="scss">
@use '../../styles/vuetify' as *;

.my-profile-status {
    text-transform: none !important;
    color: var(--v-color-white);

    &.v-btn--active {
        .v-btn__content {
            margin-bottom: -3px;
            border-bottom: 3px solid var(--v-color-green);
        }

        .v-btn__overlay {
            opacity: 0;
        }
    }

    &--compact {
        .v-btn__prepend {
            margin-inline: 0;
        }
    }

    .v-btn--highlighted {
        background-color: var(--v-color-green-darken-1);
    }

    &--hide-icon {
        span span {
            margin-left: -8px;
        }
 
        &.v-btn--active {
            .v-btn__content {
                border-bottom: none;
            }
        }
    }
}
</style>
